/* ============================================================
   Nomad Sling — Product page interactions
   - Gallery thumbnail switching
   - Variant selection (option-to-variant matching)
   - Quantity stepper
   - Sticky mobile ATC trigger (IntersectionObserver)
   - Add-on checkbox
   - Notify-me toggle
   - Reveal-on-scroll
   - Toast feedback
   ============================================================ */
(function () {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  let gallerySwapTimer;
  function galleryImageUrl(src) {
    try { return new URL(src, document.baseURI); } catch (_) { return null; }
  }
  function galleryImageKey(src) {
    const url = galleryImageUrl(src);
    if (!url) return src;
    url.searchParams.delete('width');
    return url.href;
  }
  function responsiveGallerySource(src, width) {
    const url = galleryImageUrl(src);
    // Only Shopify image endpoints support these width transformations.
    if (!url || !/^\/(?:cdn\/shop\/(?:files|products)\/|s\/files\/)/.test(url.pathname)) return null;
    const maximum = Math.min(1600, Number(width) || 1600);
    const widths = [360, 540, 750, 900, 1200, 1600].filter(value => value < maximum);
    widths.push(maximum);
    const sourceAt = value => {
      const candidate = new URL(url.href);
      candidate.searchParams.set('width', value);
      return candidate.href;
    };
    return { src: sourceAt(maximum), srcset: widths.map(value => sourceAt(value) + ' ' + value + 'w').join(', ') };
  }
  function showGalleryImage(main, thumbs, selected, src, alt, width) {
    if (!main || !src) return;
    clearTimeout(gallerySwapTimer);
    thumbs.forEach(thumb => {
      thumb.classList.toggle('is-active', thumb === selected);
      thumb.setAttribute('aria-selected', String(thumb === selected));
    });
    // Re-selecting the displayed image should not fade it or request it again.
    if (galleryImageKey(main.src) === galleryImageKey(src)) {
      main.alt = alt || ''; main.style.opacity = '1';
      return;
    }
    main.style.opacity = '0';
    gallerySwapTimer = setTimeout(() => {
      const responsive = responsiveGallerySource(src, width || selected?.dataset.bvImageWidth);
      if (responsive) main.setAttribute('srcset', responsive.srcset);
      else main.removeAttribute('srcset');
      // Keep the Liquid sizes attribute: the same gallery frame is still in use.
      main.src = responsive ? responsive.src : src;
      main.alt = alt || ''; main.style.opacity = '1';
    }, 180);
  }

  // === GALLERY ===
  function initGallery() {
    const gallery = $('[data-bv-gallery]');
    if (!gallery) return;
    const main = $('[data-bv-main]', gallery);
    const thumbs = $$('[data-bv-thumb]', gallery);
    const thumbsContainer = $('[data-bv-thumbs]', gallery);
    const prevBtn = $('[data-bv-thumbs-prev]', gallery);
    const nextBtn = $('[data-bv-thumbs-next]', gallery);

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        showGalleryImage(main, thumbs, t, t.dataset.bvSrc, t.dataset.bvImageAlt || $('img', t)?.alt);
        // Keep clicked thumb visible in the slider (only if scrollIntoView supported)
        try {
          t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } catch (_) {}
      });
    });

    // Arrow scroll behavior — scroll by 3 thumb widths each click
    if (thumbsContainer && (prevBtn || nextBtn)) {
      function scrollByThumbs(direction) {
        const firstThumb = thumbs[0];
        if (!firstThumb) return;
        const gap = parseFloat(getComputedStyle(thumbsContainer).gap) || 10;
        const step = (firstThumb.offsetWidth + gap) * 3;
        thumbsContainer.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      function updateArrowState() {
        const max = thumbsContainer.scrollWidth - thumbsContainer.clientWidth;
        const at = thumbsContainer.scrollLeft;
        if (max <= 1) {
          // Nothing to scroll — hide both arrows
          if (prevBtn) prevBtn.classList.add('is-hidden');
          if (nextBtn) nextBtn.classList.add('is-hidden');
          return;
        }
        if (prevBtn) prevBtn.classList.toggle('is-hidden', at <= 2);
        if (nextBtn) nextBtn.classList.toggle('is-hidden', at >= max - 2);
      }

      if (prevBtn) prevBtn.addEventListener('click', () => scrollByThumbs(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => scrollByThumbs(1));
      thumbsContainer.addEventListener('scroll', updateArrowState, { passive: true });
      window.addEventListener('resize', updateArrowState);
      // Initial state
      requestAnimationFrame(updateArrowState);
    }
  }

  // === VARIANT MATCHING ===
  function initVariants() {
    const variantsScript = $('[data-bv-variants]');
    if (!variantsScript) return;
    let variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch (_) { return; }

    // Variant pills are now siblings of the form (not inside it), so query document-wide
    const groups = $$('[data-bv-variant-group]');
    if (groups.length === 0) return;

    const idInput = $('[data-bv-variant-id]');
    const totalEl = $('[data-bv-total]');
    const stickyPrice = $('[data-bv-sticky-price]');
    const qtyInput = $('[data-bv-qty]');
    const hasDropdowns = groups.some(group => $('[data-bv-option-select]', group));

    function selectedOptions() {
      return groups.map((g) => {
        const active = $('.is-active[data-bv-option-value]', g);
        return active ? active.dataset.bvOptionValue : null;
      });
    }

    function findVariant(opts) {
      return variants.find((v) => {
        const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
        return opts.every((o, i) => o === vOpts[i]);
      });
    }

    function formatMoney(cents) {
      // Reads the active storefront currency/locale (not hardcoded CAD) so a second market or a
      // non-1:1 US price list formats correctly; falls back to a plain $ if Intl is unavailable.
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
      var loc = (window.Shopify && Shopify.locale) || 'en-CA';
      try {
        return new Intl.NumberFormat(loc, { style: 'currency', currency: cur }).format(cents / 100);
      } catch (_) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    function syncDropdowns(opts, variant, updateUrl) {
      groups.forEach((group, index) => {
        const select = $('[data-bv-option-select]', group);
        if (!select) return;
        select.value = opts[index];
        Array.from(select.options).forEach(option => {
          const candidateOptions = opts.slice();
          candidateOptions[index] = option.value;
          const candidate = findVariant(candidateOptions);
          option.textContent = option.value + (!candidate ? ' — Unavailable' : !candidate.available ? ' — Sold Out' : '');
        });
      });
      const stock = $('[data-bv-dropdown-stock]');
      if (stock) {
        stock.hidden = !!variant?.available;
        stock.textContent = variant ? 'Sold Out' : 'Unavailable';
      }
      const form = $('[data-bv-form]');
      if (form) {
        $$('.shopify-payment-button', form).forEach(payment => { payment.hidden = !variant?.available; });
        $$('[data-bv-notify-toggle], [data-bv-notify-form]', form).forEach(notify => {
          notify.hidden = !!variant?.available;
          $$('input', notify).forEach(input => { input.disabled = !!variant?.available; });
        });
      }
      const price = $('.bv-price');
      if (price) price.textContent = variant ? formatMoney(variant.price) : '—';
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url);
      }
    }

    function update(updateMedia = true) {
      const opts = selectedOptions();
      const v = findVariant(opts);
      if (hasDropdowns) syncDropdowns(opts, v, updateMedia);
      if (!v) {
        if (hasDropdowns) {
          if (idInput) idInput.value = '';
          if (totalEl) totalEl.textContent = '—';
          if (stickyPrice) stickyPrice.textContent = '—';
          const atc = $('[data-bv-atc]');
          if (atc) {
            atc.disabled = true;
            atc.querySelector('span').textContent = 'Unavailable';
          }
        }
        return;
      }

      if (idInput) idInput.value = v.id;
      const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      if (totalEl) totalEl.textContent = formatMoney(v.price * qty);
      if (stickyPrice) stickyPrice.textContent = formatMoney(v.price);

      // Update each "selected name" label
      groups.forEach((g, i) => {
        const lbl = $('[data-bv-selected="' + i + '"]', g);
        if (lbl) lbl.textContent = opts[i] || '';
      });

      // ATC available state
      const atc = $('[data-bv-atc]');
      if (atc) {
        atc.disabled = !v.available;
        if (!v.available) {
          atc.querySelector('span').textContent = 'Sold Out';
        } else {
          atc.querySelector('span').textContent = hasDropdowns ? atc.dataset.bvAtcLabel || 'Add to Bag' : 'Add to Bag';
        }
      }

      // Quantity changes only affect totals. Keep the shopper's chosen detail
      // photo visible, including while a gallery transition is in progress.
      if (!updateMedia) return;

      // A draft-only bag fallback covers missing catalog associations. Only an
      // exact colour prefix qualifies; unrelated lifestyle alt text cannot win.
      const gallery = $('[data-bv-gallery]');
      if (gallery) {
        const thumbs = $$('[data-bv-thumb]', gallery);
        const image = v.featured_media?.preview_image || v.featured_image;
        let selected = thumbs.find(thumb =>
          (v.featured_media?.id && String(v.featured_media.id) === thumb.dataset.bvMediaId) ||
          (image?.id && String(image.id) === thumb.dataset.bvImageId) ||
          (image?.src && thumb.dataset.bvSrc?.split('?')[0] === image.src.split('?')[0]));
        if (!image?.src && !selected && gallery.dataset.bvColourFallback === 'true') {
          const colour = opts.map(value => String(value || '').trim().toLowerCase())
            .map(value => ['crazy horse brown', 'crazy horse'].includes(value) ? 'brown' : value)
            .find(value => value === 'black' || value === 'brown');
          if (colour) selected = thumbs.find(thumb => thumb.dataset.bvMediaType === 'image' && (thumb.dataset.bvMediaAlt || '').trim().startsWith(colour + ' —'));
        }
        const src = selected?.dataset.bvSrc || image?.src;
        if (src) showGalleryImage($('[data-bv-main]', gallery), thumbs, selected, src, image?.alt || selected?.dataset.bvImageAlt || v.title || opts.filter(Boolean).join(' / '), image?.width);
      }

      // Swap variant-aware section images (lifestyle full-bleed, etc.)
      // Reads data-bv-bg-<color> attributes from the section bg, falls back to default.
      const colorOpt = (opts || []).find((val) => /black|brown|cognac|natural|tan/i.test(val || ''));
      const slug = colorOpt ? colorOpt.toLowerCase().replace(/\s+/g, '-') : 'default';
      $$('[data-bv-lifestyle-bg]').forEach((bg) => {
        const img = $('[data-bv-lifestyle-img]', bg);
        if (!img) return;
        const candidates = [
          bg.dataset['npBg' + slug.charAt(0).toUpperCase() + slug.slice(1)], // e.g. npBgBlack
          bg.dataset.bvBgDefault
        ];
        const newSrc = candidates.find(Boolean);
        if (newSrc && img.src.indexOf(newSrc) === -1) {
          img.style.opacity = '0';
          setTimeout(() => { img.src = newSrc; img.style.opacity = '1'; }, 180);
        }
      });
    }

    groups.forEach((group) => {
      const select = $('[data-bv-option-select]', group);
      if (select) select.addEventListener('change', () => {
        const option = $$('[data-bv-option-value]', group).find(button => button.dataset.bvOptionValue === select.value);
        if (option) option.click();
      });
      $$('[data-bv-option-value]', group).forEach((btn) => {
        btn.addEventListener('click', () => {
          $$('[data-bv-option-value]', group).forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          update();
        });
      });
    });

    if (qtyInput) qtyInput.addEventListener('input', () => update(false));
    // The visible dropdown wins at startup. A size picked before this script ran
    // was being reset to the server default (28"-30") and that size was added.
    let pickedEarly = false;
    groups.forEach((group) => {
      const select = $('[data-bv-option-select]', group);
      const option = select && $$('[data-bv-option-value]', group).find(button => button.dataset.bvOptionValue === select.value);
      if (!option || option.classList.contains('is-active')) return;
      $$('[data-bv-option-value]', group).forEach((b) => b.classList.remove('is-active'));
      option.classList.add('is-active');
      pickedEarly = true;
    });
    if (hasDropdowns) update(pickedEarly);
  }

  // === QTY STEPPER ===
  function initQty() {
    const minus = $('[data-bv-qty-minus]');
    const plus = $('[data-bv-qty-plus]');
    const input = $('[data-bv-qty]');
    if (!input) return;

    function clamp(v) { return Math.max(1, Math.min(10, v)); }

    if (minus) minus.addEventListener('click', () => {
      input.value = clamp((parseInt(input.value, 10) || 1) - 1);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    if (plus) plus.addEventListener('click', () => {
      input.value = clamp((parseInt(input.value, 10) || 1) + 1);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  // === STICKY ATC ===
  function initStickyATC() {
    const sticky = $('[data-bv-sticky]');
    if (!sticky || sticky.dataset.bvStickyReady === 'true') return;
    const trigger = $('[data-bv-sticky-trigger]', sticky);
    if (!trigger) return;
    sticky.dataset.bvStickyReady = 'true';
    const reveal = visible => {
      sticky.classList.toggle('is-visible', visible);
      sticky.inert = !visible;
      sticky.setAttribute('aria-hidden', String(!visible));
    };
    const anchor = $('.bv-hero') || $('[data-bv-form]');
    reveal(true);
    if (anchor && typeof window.IntersectionObserver === 'function') {
      try {
        const rect = anchor.getBoundingClientRect();
        reveal(rect.bottom <= 0 || rect.top >= window.innerHeight);
        const observer = new window.IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.target === anchor) reveal(!entry.isIntersecting); });
        }, { threshold: 0 });
        observer.observe(anchor);
      } catch (_) { reveal(true); }
    }
    trigger.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const hero = $('[data-bv-atc]');
      if (trigger.disabled || hero && hero.disabled) return;
      if (hero) { hero.click(); return; }
      bvCartError('The bag button could not be found. Refresh the page before adding this bag.');
    }, true);
  }

  // === ADD-ON ===
  function initAddon() {
    const addon = $('[data-bv-addon]');
    if (!addon) return;
    const input = $('[data-bv-addon-input]', addon);
    const check = $('[data-bv-addon-check]', addon);

    addon.addEventListener('click', (e) => {
      // checkbox label handles it natively, but we also toggle visual state
      setTimeout(() => {
        const checked = input.checked;
        addon.classList.toggle('is-checked', checked);
        if (check) check.style.display = checked ? 'block' : 'none';
      }, 0);
    });
  }

  // === NOTIFY-ME ===
  function initNotify() {
    const toggle = $('[data-bv-notify-toggle]');
    const form = $('[data-bv-notify-form]');
    if (!toggle || !form) return;

    toggle.addEventListener('click', () => {
      form.classList.add('is-open');
      toggle.style.display = 'none';
      const input = form.querySelector('input');
      if (input) input.focus();
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = form.querySelector('input').value.trim();
      if (!email) return;
      // TODO: wire to Klaviyo back-in-stock endpoint or theme's email API
      form.innerHTML = '<div style="padding:16px 0; color: var(--bv-success); font-size:14px;">✓ We\'ll email you when it\'s back. Thanks.</div>';
      toast("You're on the list. We'll email you.");
    });
  }

  // === ATC SUBMIT ===
  // One path for the hero and sticky button: prepare engraving, add the bag/fee
  // and any selected Care Kit together, then refresh the real cart drawer.
  // XHR avoids the storefront's Rebuy fetch wrapper.
  let bvAdding = false;
  function bvXhrJson(method, url, bodyObj) {
    return new Promise(function (resolve, reject) {
      var x = new XMLHttpRequest();
      x.open(method, url, true);
      x.timeout = 20000;
      x.setRequestHeader('Accept', 'application/json');
      if (bodyObj) x.setRequestHeader('Content-Type', 'application/json');
      x.onload = function () {
        var result;
        try { result = JSON.parse(x.responseText); }
        catch (_) { reject(new Error('The cart response could not be read.')); return; }
        if (x.status >= 200 && x.status < 300) resolve(result);
        else reject(new Error(typeof result.description === 'string' ? result.description : 'The items could not be added.'));
      };
      x.onerror = x.ontimeout = function () { reject(new Error('The connection was interrupted. Check your cart before trying again.')); };
      x.send(bodyObj ? JSON.stringify(bodyObj) : null);
    });
  }
  function bvCartError(message) {
    if (window.BellvariEngravingCart) return window.BellvariEngravingCart.showError(message, $('[data-bv-atc]'));
    var error = document.getElementById('bv-product-cart-error');
    if (!error) {
      error = document.createElement('p');
      error.id = 'bv-product-cart-error';
      error.setAttribute('role', 'alert');
      error.tabIndex = -1;
      error.style.cssText = 'padding:12px 0;color:#702819;font-size:16px;line-height:1.5;';
      var host = $('[data-bv-form]') || document.querySelector('main') || document.body;
      host.appendChild(error);
    }
    error.textContent = message;
    error.hidden = false;
    error.focus();
  }
  window.bvAddToCart = async function (variantId, quantity) {
    if (bvAdding) return { ok: false, busy: true };
    const id = parseInt(variantId, 10);
    const qty = Number(quantity == null ? 1 : quantity);
    const isProductAdd = parseInt(($('[data-bv-variant-id]') || {}).value, 10) === id;
    const bridge = window.BellvariEngraving;
    if (!id || !Number.isSafeInteger(qty) || qty < 1) {
      bvCartError('Choose an available bag and a whole quantity of one or more.');
      return { ok: false };
    }
    bvAdding = true;
    const buttons = $$('[data-bv-atc], [data-bv-sticky-trigger]').map(button => ({ button, disabled: button.disabled }));
    buttons.forEach(({ button }) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    const cart = window.BellvariEngravingCart;
    const drawer = document.querySelector('cart-drawer');
    let prepared = null;
    let confirmedAdd = false;
    const checkVariant = () => {
      if (isProductAdd && parseInt(($('[data-bv-variant-id]') || {}).value, 10) !== id) {
        throw new Error('The bag colour changed while we checked the engraving. Review your current colour and save the engraving again.');
      }
    };
    const notifyAdded = () => {
      if (isProductAdd && bridge && typeof bridge.added === 'function') {
        try { bridge.added(); } catch (_) { /* A confirmed cart add must not become a retry after a UI reset error. */ }
      }
    };
    const checkPurchase = async () => {
      checkVariant();
      if (isProductAdd && bridge && typeof bridge.assertCurrent === 'function') {
        await bridge.assertCurrent({ variantId: String(id), quantity: qty });
      }
      checkVariant();
    };
    try {
      if (cart) {
        cart.clearError();
        // Resolve any uncertain previous attempt before creating a new personalization ID.
        const recovery = await cart.reconcilePending();
        if (recovery.status === 'added') { notifyAdded(); return { ok: true, recovered: true, response: recovery.response }; }
      }
      checkVariant();
      if (isProductAdd && bridge && typeof bridge.prepare === 'function') {
        if (!cart) throw new Error('Engraving could not be loaded safely. Refresh the page before adding this bag.');
        prepared = await bridge.prepare({ variantId: String(id), quantity: qty });
        if (prepared && !prepared.ok) throw new Error(prepared.error || 'Review and confirm your engraving before adding the bag.');
      }
      checkVariant();
      let items = prepared ? prepared.items : [{ id: id, quantity: qty }];
      if (!Array.isArray(items) || !items.length) throw new Error('The engraving selection is incomplete. Open it and save again.');
      items = items.slice();
      const addon = isProductAdd ? $('[data-bv-addon-input]') : null;
      const addonId = addon && addon.checked ? parseInt(addon.dataset.bvAddonVariant, 10) : null;
      if (addon && addon.checked && !addonId) throw new Error('The selected add-on is unavailable. Review it before adding the bag.');
      if (addonId) {
        const item = { id: addonId, quantity: 1 };
        const label = addon.closest('[data-bv-addon]');
        const bag = prepared && items.find(line => line.properties && line.properties._bellvari_role === 'bag');
        if (bag && label && label.hasAttribute('data-bv-addon-gift')) {
          item.properties = { _bv_engraving_gift_for: bag.properties._bellvari_personalization_id };
        } else if (!prepared && label && label.hasAttribute('data-bv-addon-gift')) {
          // A complimentary kit belongs to this plain-bag add, too. Separate
          // private fields keep it distinct from engraving and paid Care Kits.
          if (typeof window.crypto?.randomUUID !== 'function') throw new Error('We could not link the complimentary Care Kit to your bag. Refresh the page before trying again.');
          const groupId = window.crypto.randomUUID();
          items[0] = { ...items[0], properties: { ...items[0].properties, _bv_gift_group: groupId, _bv_gift_role: 'bag' } };
          item.properties = { _bv_gift_group: groupId, _bv_gift_role: 'gift' };
        }
        items.push(item);
      }
      if (drawer && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(document.activeElement);
      const sectionIds = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map(item => item.id).join(',') : 'cart-drawer,cart-icon-bubble';
      const options = { sections: sectionIds, sections_url: window.location.pathname };
      const response = cart ? await cart.guardedAdd(items, options, checkPurchase) : await bvXhrJson('POST', '/cart/add.js', { items: items, ...options });
      confirmedAdd = true;
      notifyAdded();
      if (cart) {
        if (response.sections && Object.values(response.sections).some(html => typeof html === 'string' && html.length)) cart.render(response, true);
        else await cart.refresh(true);
      } else if (drawer && typeof drawer.renderContents === 'function' && response.sections) drawer.renderContents(response);
      else {
        ['cart:refresh', 'cart:updated', 'cart:item-added', 'cart:build', 'ajaxProduct:added'].forEach(name => document.dispatchEvent(new CustomEvent(name, { bubbles: true })));
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }
      if (addonId) {
        const label = addon.closest('[data-bv-addon]');
        const isGift = label && label.hasAttribute('data-bv-addon-gift');
        if (!isGift) {
          addon.checked = false;
          if (label) label.classList.remove('is-checked');
          const mark = label && $('[data-bv-addon-check]', label);
          if (mark) mark.style.display = 'none';
        }
      }
      document.dispatchEvent(new CustomEvent('bellvari:cart-added', { detail: { items: items, selection: prepared, response: response } }));
      return { ok: true, response: response };
    } catch (error) {
      if (confirmedAdd) {
        bvCartError('Your items were added. Open your cart to review them; the cart display could not refresh.');
        return { ok: true, cartRefreshFailed: true };
      }
      bvCartError(error.message || 'The items could not be added. Please review your cart before trying again.');
      return { ok: false, error: error.message };
    } finally {
      if (isProductAdd && bridge && typeof bridge.finish === 'function') bridge.finish();
      bvAdding = false;
      const currentId = parseInt(($('[data-bv-variant-id]') || {}).value, 10);
      let unavailable = false;
      try {
        const variant = JSON.parse(($('[data-bv-variants]') || {}).textContent || '[]').find(item => item.id === currentId);
        unavailable = !!variant && !variant.available;
      } catch (_) {}
      buttons.forEach(({ button, disabled }) => { button.disabled = disabled || unavailable; button.removeAttribute('aria-busy'); });
    }
  };
  function initATCToast() {
    const wrapper = $('[data-bv-form]');
    if (!wrapper) return;
    const add = e => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const button = $('[data-bv-atc]', wrapper);
      if (button && button.disabled) return;
      const id = ($('[data-bv-variant-id]') || {}).value;
      const quantity = ($('[data-bv-qty]') || {}).value || 1;
      window.bvAddToCart(id, quantity);
    };
    // The Shopify form is inside this DIV. Catch clicks before form/app handlers
    // can consume them, including the sticky button's hero-button click proxy.
    wrapper.addEventListener('click', e => {
      const button = e.target.closest && e.target.closest('[data-bv-atc]');
      if (button && wrapper.contains(button)) add(e);
    }, true);
    wrapper.addEventListener('submit', add, true);
  }

  function toast(msg) {
    const t = $('[data-bv-toast]');
    if (!t) return;
    const m = $('[data-bv-toast-msg]', t);
    if (m) m.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(window._npToast);
    window._npToast = setTimeout(() => t.classList.remove('is-visible'), 3000);
  }

  // === REVEAL ON SCROLL ===
  function initReveal() {
    const els = $$('.bv-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  // === FAQ — auto-close siblings (optional, accordion-style) ===
  function initFAQ() {
    const items = $$('.bv-faq-item');
    items.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) {
          items.forEach((other) => { if (other !== d) other.removeAttribute('open'); });
        }
      });
    });
  }

  // === COLORWAY CARDS — click → select that variant + scroll to top of gallery ===
  function initColorways() {
    const cards = $$('[data-bv-colorway]');
    if (cards.length === 0) return;

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const variantValue = card.dataset.bvColorway;
        if (!variantValue) return;

        // Find the matching variant pill / color swatch in the picker and click it
        const matchingOpt = $$('[data-bv-option-value]').find(
          (b) => b.dataset.bvOptionValue.toLowerCase() === variantValue.toLowerCase()
        );
        if (matchingOpt) {
          matchingOpt.click();
          // Update active state on colorway cards
          cards.forEach((c) => c.classList.toggle(
            'is-active',
            c.dataset.bvColorway.toLowerCase() === variantValue.toLowerCase()
          ));
          // Smooth-scroll up to the gallery so the customer sees the variant they selected
          const gallery = $('[data-bv-gallery]');
          if (gallery) gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // === BOOT ===
  function boot() {
    initGallery();
    initVariants();
    initQty();
    initStickyATC();
    initAddon();
    initNotify();
    initATCToast();
    initReveal();
    initFAQ();
    initColorways();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/* ============================================================
   STAT COUNTER ANIMATION (for ed-stat-num[data-counter])
   ============================================================ */
(function () {
  if (typeof window === 'undefined') return;
  document.addEventListener('DOMContentLoaded', function () {
    var stats = document.querySelectorAll('[data-counter]');
    if (!('IntersectionObserver' in window) || stats.length === 0) return;
    var animate = function (el) {
      var target = parseFloat(el.dataset.counter);
      if (isNaN(target)) return;
      var original = el.textContent;
      var isPercent = original.indexOf('%') !== -1;
      var isInches = original.indexOf('"') !== -1;
      var isMultiplier = original.indexOf('×') !== -1;
      var duration = 1400;
      var start = performance.now();
      var tick = function (now) {
        var t = Math.min((now - start) / duration, 1);
        var eased = 1 - Math.pow(1 - t, 3);
        var val = Math.round(target * eased);
        el.textContent = val + (isPercent ? '%' : '') + (isInches ? '"' : '') + (isMultiplier ? '×' : '');
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          animate(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });
    stats.forEach(function (s) { io.observe(s); });
  });

  // === SIZE-GUIDE MODAL (belts) ===
  (function initSizeGuide() {
    function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
    ready(function () {
      var modal = document.querySelector('[data-bv-sizeguide-modal]');
      if (!modal) return;
      var panel = modal.querySelector('.bv-sg-modal-panel');
      var opener = null;
      function open(trigger) {
        opener = trigger || null;
        modal.hidden = false;
        document.body.classList.add('bv-sg-open');
        if (panel) panel.focus();
        document.addEventListener('keydown', onKey);
      }
      function close() {
        modal.hidden = true;
        document.body.classList.remove('bv-sg-open');
        document.removeEventListener('keydown', onKey);
        if (opener && typeof opener.focus === 'function') opener.focus();
      }
      function onKey(e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key === 'Tab' && panel) {
          var f = panel.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
      document.querySelectorAll('[data-bv-sizeguide-open]').forEach(function (btn) {
        btn.addEventListener('click', function () { open(btn); });
      });
      modal.querySelectorAll('[data-bv-sizeguide-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
    });
  })();
})();
